import React, { useCallback, useEffect, useRef, useState } from "react";
import L from "leaflet";
import {
  MapContainer,
  ImageOverlay,
  FeatureGroup,
  useMap,
} from "react-leaflet";
import useLocalStorageState from "use-local-storage-state";
import cs from "classnames";
import { EditControl } from "react-leaflet-draw";

import { useDefineBounds } from "../lib/useDefineBounds";
import { useMarker } from "../lib/useMarkerData";
import { useAuthContext } from "../../../lib/context/Auth/AuthContext";
import { useShapeHandlers } from "../lib/useShapeHandlers";

import { drawMarkers } from "../../../lib/helpers/leaflet";
import { getImageSize } from "../../../api/external-api/react-native-map-view/utils/overlay.utils";

import { FloorSettingsButton } from "../FloorSettings/FloorSettingsButton";
import { floorPlanHelpers } from "../lib/floorPlanHelpers";
import { LoadingBox } from "../../shared/LoadingBox/LoadingBox";
import { EmptyEntity } from "../../shared/EmptyEntity/EmptyEntity";
import { FloorFields } from "../../../api/graphql/floors/floors";
import { FloorChildrenDefaultList } from "../FloorChildrenList/FloorChildrenDefaultList";
import { FloorMarkersSettings } from "../FloorMarkersSettings/FloorMarkersSettings";
import { FloorMapActionsGroup } from "../FloorMapActionsGroup/FloorMapActionsGroup";
import { Size } from "./FloorsPlan";

import "leaflet-draw/dist/leaflet.draw.css";
import "./FloorContent.scss";

interface Props {
  data: FloorFields;
  openMapSettings: boolean;
}

interface FloorPlanMapControllerProps {
  mapBounds: L.LatLngBoundsExpression | null;
  mapMaxBounds: L.LatLngBoundsExpression | null;
  mapImageSize: Size;
  poiMarkers: L.Marker[];
  markers: L.Marker[];
  circles: L.Layer[];
  polygonal: L.Layer[];
  handleShapeClick: () => void;
  showGridline: boolean;
  showTooltips: boolean;
  showRoomMarkers: boolean;
  markersScaleFactor: number;
  hasContrastTooltips: boolean;
}

const FloorPlanMapController = ({
  mapBounds,
  mapMaxBounds,
  mapImageSize,
  poiMarkers,
  markers,
  circles,
  polygonal,
  handleShapeClick,
  showGridline,
  showTooltips,
  showRoomMarkers,
  markersScaleFactor,
  hasContrastTooltips,
}: FloorPlanMapControllerProps) => {
  const map = useMap();
  const [mapReady, setMapReady] = useState(false);

  useEffect(() => {
    if (!mapBounds || !mapMaxBounds) {
      setMapReady(false);
      return;
    }

    let animationFrameId: number | undefined;
    const handleMoveEnd = () => {
      animationFrameId = requestAnimationFrame(() => {
        setMapReady(true);
      });
    };

    setMapReady(false);
    map.setMaxBounds(mapMaxBounds);
    map.once("moveend", handleMoveEnd);
    map.fitBounds(mapBounds);

    return () => {
      map.off("moveend", handleMoveEnd);

      if (animationFrameId !== undefined) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [map, mapBounds, mapMaxBounds]);

  useEffect(() => {
    if (!mapReady) {
      return;
    }

    return drawMarkers({
      map,
      poiMarkers,
      markers,
      layers: [...circles, ...polygonal],
      handleShapeClick,
      config: {
        mapSize: {
          width: mapImageSize.width / 2,
          height: mapImageSize.height / 2,
        },
      },
      options: {
        snapEnabled: showGridline,
        showTooltips,
        showRoomMarkers,
        scaleFactor: markersScaleFactor,
        hasContrastTooltips,
      },
    });
  }, [
    circles,
    handleShapeClick,
    hasContrastTooltips,
    map,
    mapImageSize,
    mapReady,
    markers,
    markersScaleFactor,
    poiMarkers,
    polygonal,
    showGridline,
    showRoomMarkers,
    showTooltips,
  ]);

  return null;
};

export const FloorPlan = ({ data, openMapSettings }: Props) => {
  const [activeEditDraw, setActiveEditDraw] = useState(false);
  const [timeOutEdit, setTimeOutEdit] = useState(false);
  const [mapImageSize, setMapImageSize] = useState<Size | null>(null);
  const [activeRoomIdDraw, setActiveRoomIdDraw] = useState<string | undefined>(
    undefined
  );
  const [editableLayers, setEditableLayers] = useState<L.FeatureGroup | null>(
    null
  );
  const editableLayersRef = useRef<L.FeatureGroup | null>(null);

  const { handleCreateShape, handleEditShape, handleDeleteShape } =
    useShapeHandlers();
  const { user } = useAuthContext();

  const { validateInputNumber } = floorPlanHelpers();
  const [markersScaleFactor, setMarkersScaleFactor] = useLocalStorageState(
    `markers-scale-factor-${data?.id}`,
    {
      defaultValue: 0.7,
    }
  );

  const {
    markers,
    poiMarkers,
    openMarkerPopup,
    openShapePopup,
    shapes: { _circles: circles, _polygonal: polygonal },
  } = useMarker(data, {
    scaleFactor: markersScaleFactor,
    showRoomMarkers: data?.map?.showRoomMarkers || false,
    activeDraw: activeEditDraw,
    hasContrastTooltips: data?.map?.hasContrastTooltips || false,
  });

  const { overlayBounds, mapBounds, mapMaxBounds } =
    useDefineBounds(mapImageSize);

  useEffect(() => {
    setMapImageSize(null);

    getImageSize(data?.map?.url || "").then(setMapImageSize);
  }, [data?.map?.url]);

  useEffect(() => {
    setActiveEditDraw(false);
    setActiveRoomIdDraw(undefined);
  }, [data?.id]);

  useEffect(() => {
    if (!data?.map?.url) {
      return;
    }

    if (!editableLayers) {
      return;
    }

    const layers = [...circles, ...polygonal];

    layers.forEach((layer) => {
      if (layer && layer.addTo) {
        layer.addTo(editableLayers);
      }
    });

    return () => {
      layers.forEach((layer) => {
        editableLayers.removeLayer(layer);
      });
    };
  }, [circles, data?.map?.url, editableLayers, polygonal]);

  const handleShapeClick = useCallback(() => {
    if (activeEditDraw) {
      return;
    }

    //automatically focus edit button when toolbar is shown:
    const editButton = document.querySelector(".leaflet-draw-edit-edit");

    if (editButton) {
      (editButton as HTMLElement).click();
    }

    setActiveRoomIdDraw(undefined);

    setActiveEditDraw(true);
  }, [activeEditDraw]);

  const handleEditableLayersRef = useCallback(
    (featureGroup: L.FeatureGroup | null) => {
      editableLayersRef.current = featureGroup;
      setEditableLayers(featureGroup);
    },
    []
  );

  const handleAddRoomButtonClick = (id?: string) => {
    setActiveEditDraw(false);

    if (sessionStorage.getItem("activeRoomIdDraw")?.length) {
      sessionStorage.removeItem("activeRoomIdDraw");
    }

    sessionStorage.setItem("activeRoomIdDraw", id || "");
    setActiveRoomIdDraw(id);
  };

  const classes = cs("FloorContent__plan", {
    "FloorContent__plan--hide": !activeEditDraw && !activeRoomIdDraw,
    "FloorContent__plan--hideDraw": activeEditDraw,
    "FloorContent__plan--hideSection": activeRoomIdDraw,
  });

  return (
    <div className="FloorContent">
      <div className={classes}>
        {data?.map === null ? (
          <EmptyEntity
            type="FloorMap"
            typeOfFloorMap="floor"
            floorId={data?.id}
          />
        ) : (
          <>
            <MapContainer
              id="map-container"
              crs={L.CRS.Simple}
              center={[0, 0]}
              zoom={0}
              maxZoom={3}
              minZoom={-3}
              zoomSnap={0.15}
              zoomDelta={0.5}
              doubleClickZoom={false}
              scrollWheelZoom={true}
              touchZoom={true}
            >
              <FloorMapActionsGroup>
                <FloorSettingsButton
                  map={data?.map}
                  openMapSettings={openMapSettings}
                />
                <FloorMarkersSettings
                  scaleFactor={markersScaleFactor}
                  onScaleFactorChange={setMarkersScaleFactor}
                />
              </FloorMapActionsGroup>
              {data?.map?.url !== undefined &&
              !!overlayBounds &&
              !!mapImageSize ? (
                <>
                  <FloorPlanMapController
                    mapBounds={mapBounds}
                    mapMaxBounds={mapMaxBounds}
                    mapImageSize={mapImageSize}
                    poiMarkers={poiMarkers}
                    markers={markers}
                    circles={circles}
                    polygonal={polygonal}
                    handleShapeClick={handleShapeClick}
                    showGridline={data?.map?.showGridline || false}
                    showTooltips={data?.map?.showTooltips || false}
                    showRoomMarkers={data?.map?.showRoomMarkers || false}
                    markersScaleFactor={markersScaleFactor}
                    hasContrastTooltips={data?.map?.hasContrastTooltips || false}
                  />
                  <FeatureGroup ref={handleEditableLayersRef}>
                    <EditControl
                      position="topright"
                      draw={{
                        circlemarker: false,
                        marker: false,
                        polyline: false,
                      }}
                      // onMounted={handleShapesOnMount}
                      onCreated={async (e) => {
                        try {
                          await handleCreateShape({
                            drawEvent: e,
                            setActiveRoomIdDraw,
                            tenantId: user?.customerid || "",
                          });
                        } finally {
                          const layerGroup = editableLayersRef.current;

                          if (layerGroup?.hasLayer(e.layer)) {
                            layerGroup.removeLayer(e.layer);
                          } else {
                            e.layer.remove();
                          }
                        }
                      }}
                      onEdited={async (e) => {
                        if (timeOutEdit) return;

                        setTimeOutEdit(true);
                        await handleEditShape({
                          editEvent: e,
                          tenantId: user?.customerid || "",
                        });

                        setActiveEditDraw(false);

                        setActiveRoomIdDraw(undefined);

                        setTimeout(() => {
                          setTimeOutEdit(false);
                        }, 7000);
                      }}
                      onEditStop={() => {
                        setActiveEditDraw(false);
                        setActiveRoomIdDraw(undefined);
                      }}
                      onDeleted={(e) => {
                        handleDeleteShape({
                          editEvent: e,
                          tenantId: user?.customerid || "",
                        });
                      }}
                      onDeleteStop={(e) => {
                        setActiveEditDraw(false);
                        setActiveRoomIdDraw(undefined);
                      }}
                      onEditStart={() => {
                        if (!activeEditDraw) {
                          setActiveEditDraw(true);
                        }
                      }}
                      onDeleteStart={() => {
                        if (!activeEditDraw) {
                          setActiveEditDraw(true);
                        }
                      }}
                    />
                  </FeatureGroup>
                  <ImageOverlay
                    url={data.map.url}
                    opacity={
                      data.map.opacity && validateInputNumber(data.map.opacity)
                    }
                    bounds={overlayBounds}
                  />
                </>
              ) : (
                <LoadingBox />
              )}
            </MapContainer>
          </>
        )}
      </div>
      <div className="FloorContent__children FloorContent__children--view">
        <FloorChildrenDefaultList
          onClick={openMarkerPopup}
          onClickShape={openShapePopup}
          data={data}
          handleAddRoomButtonClick={handleAddRoomButtonClick}
        />
      </div>
    </div>
  );
};
